import { Box } from '@material-ui/core';
import { StepperComponent } from '@roc/ui';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { alpha, makeStyles, Theme } from '@material-ui/core/styles';
import { useLoanStore } from '@roc/feature-loans';
import { LoanStatus } from '@roc/feature-utils';
import { Cancel, CancelOutlined, CheckCircle, CheckRounded, Close, ErrorOutline, RadioButtonChecked } from '@material-ui/icons';
import {
  stepsByClosingStatus,
  stepsByLoanStatus,
} from '../utils/loanDetailsConstants';
import { ClosingStage } from '../utils/loanDetailsTypes';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) => ({
  stepper: {
    padding: theme.spacing(2.5),
    margin: 'auto',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },

    // Connector: thin light track, primary once the step is reached
    '& .MuiStepConnector-alternativeLabel': {
      top: '12px',
      left: 'calc(-50% + 20px)',
      right: 'calc(50% + 20px)',
    },
    '& .MuiStepConnector-lineHorizontal': {
      borderTopWidth: '2px',
      borderColor: '#DADEF4',
    },
    '& .MuiStepConnector-active .MuiStepConnector-lineHorizontal, & .MuiStepConnector-completed .MuiStepConnector-lineHorizontal': {
      borderColor: theme.palette.primary.main,
    },
    // Labels: muted for upcoming steps, dark for done / current
    '& .MuiStepLabel-labelContainer': {
      '& .MuiStepLabel-label': {
        marginTop: '8px',
      },
      '& span': {
        color: `${theme.palette.text.secondary} !important`,
        fontSize: '14px !important',
        fontWeight: 'normal !important',
      },
      '& .MuiStepLabel-active span, & .MuiStepLabel-completed span': {
        color: `${theme.palette.text.primary} !important`,
        fontWeight: '500 !important',
      },
    },
  },
  stepDot: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  upcomingDot: {
    backgroundColor: '#FFFFFF',
    border: '2px solid #CED3F0',
  },
  completedDot: {
    backgroundColor: theme.palette.primary.main,
    '& .MuiSvgIcon-root': {
      color: '#FFFFFF !important',
      backgroundColor: 'transparent !important',
      fontSize: 16,
    },
  },
  activeDot: {
    backgroundColor: '#FFFFFF',
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
    '&::after': {
      content: '""',
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
    },
  },
  statusStepIcon: {
    width: 26,
    height: 26,
    color: theme.palette.primary.main,
    '& svg': {
      backgroundColor: 'transparent !important',
      fontSize: '30px',
      margin: '-2px',
    },
  },
  pendingSubmissionStepIcon: {
    '& svg': {
      color: `#83000c !important`,
    },
  },
  negativeStepIcon: {
    '& svg': {
      color: '#83000c !important',
    },
  },
  activeStepIcon: {
    '& svg': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  completedStepIcon: {
    '& svg': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
}));

export const LoanDetailsStepper = observer(
  ({ stepsByStatus, currentStatus }) => {
    const classes = useStyles();

    const steps = stepsByStatus?.[currentStatus] ?? [];

    const getIcon = (step) => {
      if (step.warning) {
        return <StatusIcon icon={<ErrorOutline />} className={clsx(classes.statusStepIcon, classes.pendingSubmissionStepIcon)} />
      }
      else if (step.negative) {
        return <StatusIcon icon={<CancelOutlined />} className={clsx(classes.statusStepIcon, classes.negativeStepIcon)} />
      }
      else if (step.active) {
        return <Box className={clsx(classes.stepDot, classes.activeDot)} />
      }
      else if (step.completed) {
        return <Box className={clsx(classes.stepDot, classes.completedDot)}><CheckRounded /></Box>
      }
      // upcoming step: hollow outlined circle
      return <Box className={clsx(classes.stepDot, classes.upcomingDot)} />;
    }

    return (
      <>
        <StepperComponent
          activeStep={steps.findLastIndex(s => s.completed)}
          disableNavigation={true}
          handleStep={() => { }}
          steps={steps.map(step => ({
            ...step,
            icon: getIcon(step),
            onBack: () => { },
            onNext: () => { },
            allowBack: false,
            allowNext: true,
          }))}
          className={classes.stepper}
          alternativeLabel
        />
      </>
    );
  }
);

const StatusIcon = ({
  icon,
  className
}) => {
  const classes = useStyles();
  return (
    <Box className={className}>
      {icon}
    </Box>
  );
};

export const LoanStatusStepper = observer(() => {
  const { loanStore } = useLoanStore();
  const { loanDetails } = loanStore;

  return (
    <LoanDetailsStepper
      stepsByStatus={stepsByLoanStatus}
      currentStatus={loanDetails?.status}
    />
  );
});

export const ClosingStatusStepper = observer(() => {
  const { loanStore } = useLoanStore();

  const closingStage: string =
    (loanStore.closingRequirements?.loanClosingStage as any) ??
    ClosingStage.UNDEFINED;

  return (
    <LoanDetailsStepper
      stepsByStatus={stepsByClosingStatus}
      currentStatus={closingStage?.trim()}
    />
  );
});
